import { Document, Page, Text, View } from '@react-pdf/renderer';
import { useMemo } from 'react';
import styles from '../../styles/features/pending-aproves/pending-aplication-report-pdf.styles';
import {
  Aplicacao,
  AuditoriaAplicacaoObjeto,
  AuditoriaAplicacaoObjetoProduto,
  NotificacoesResponse,
} from '../../@types';
import { formatStringDate } from '../utils';

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <View style={styles.infoRow}>
    <Text style={styles.label}>{label} </Text>
    <Text>{value}</Text>
  </View>
);

interface PendingAplicationReportDocProps {
  notifications: NotificacoesResponse;
}

type SementesRow = {
  origem: string;
  destino: string;
  quantidadeTotal: string;
};

const formatQuantidade = (value: number | string) =>
  Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

const buildSementesRow = (aplicacao: Aplicacao): SementesRow | undefined => {
  const { sementes } = aplicacao;
  if (!sementes) return;

  const tratado = sementes.origem?.tratado;
  const quantidade = tratado?.quantidade || 0;
  const abreviatura = tratado?.abreviatura?.trim() ?? '';

  return {
    origem: sementes.origem?.descricao?.trim() || '--',
    destino: sementes.destino?.descricao?.trim() || '--',
    quantidadeTotal: `${formatQuantidade(quantidade)} ${abreviatura}`,
  };
};

const getSufixoDoseTratamentoSemente = (aplicacao: Aplicacao): string => {
  const abreviatura = aplicacao.sementes?.origem?.tratado?.abreviatura?.trim();

  if (abreviatura) return `100${abreviatura}`;

  return 'ha';
};

const produtosMapped = (
  produtos: AuditoriaAplicacaoObjetoProduto[],
  isTratamentoSementes: boolean,
  aplicacao: Aplicacao,
) =>
  produtos.map((produto) => {
    const sufixoDose = isTratamentoSementes
      ? getSufixoDoseTratamentoSemente(aplicacao)
      : 'ha';

    return {
      descricao: produto.descricao,
      dose: `${formatQuantidade(
        produto.dose,
      )} ${produto.unidade.abreviatura.trim()}/${sufixoDose}`,
      quantidadeTotal: `${formatQuantidade(
        produto.quantidadeTotal,
      )} ${produto.unidade.abreviatura.trim()}`,
    };
  });

const PendingAplicationReportDoc = ({
  notifications,
}: PendingAplicationReportDocProps) => {
  const now = new Date();
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  const generatedAt = `${now.toLocaleDateString(
    'pt-BR',
  )}, ${now.toLocaleTimeString('pt-BR', timeOptions)}`;
  const currentUrl = window.location.href;

  const sections = useMemo(
    () =>
      [...notifications.aplicacoes]
        .sort(
          (a, b) =>
            new Date(a.auditoriaAplicacao.dtIni).getTime() -
            new Date(b.auditoriaAplicacao.dtIni).getTime(),
        )
        .map((aplicacao) => {
          const auditoriaObjeto = JSON.parse(
            aplicacao.auditoriaAplicacao.objeto,
          ) as AuditoriaAplicacaoObjeto;
          const isTratamentoSementes =
            aplicacao.tipoOperacaoEnum === 'operacao_tratamento_semente';

          return {
            tipoAplicacao: aplicacao.tipoOperacao,
            id: aplicacao.id,
            iniciadaEm: formatStringDate(aplicacao.auditoriaAplicacao.dtIni),
            encerradaEm: formatStringDate(aplicacao.auditoriaAplicacao.dtFim),
            operador: aplicacao.usuario.nome,
            propriedade: aplicacao.propriedade.descricao,
            talhao: `${aplicacao.gleba.descricao} (${aplicacao.auditoriaAplicacao.area} ha)`,
            cultura: aplicacao.cultura.descricao,
            area: `${aplicacao.auditoriaAplicacao.area} ha`,
            isTratamentoSementes,
            produtos: produtosMapped(
              auditoriaObjeto.produtos ?? [],
              isTratamentoSementes,
              aplicacao,
            ),
            sementes: isTratamentoSementes
              ? buildSementesRow(aplicacao)
              : undefined,
            observacao: aplicacao.auditoriaAplicacao.obs,
          };
        }),
    [notifications],
  );

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.pageHeader} fixed>
          <Text style={styles.headerSide}>{generatedAt}</Text>
          <Text style={styles.headerCenter}>{currentUrl}</Text>
          <Text style={styles.headerSide}> </Text>
        </View>

        {sections.map((section) => (
          <View key={section.id} style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>{section.tipoAplicacao}</Text>

            <InfoRow label="Iniciada em:" value={section.iniciadaEm} />
            <InfoRow label="Encerrada em:" value={section.encerradaEm} />
            <InfoRow label="Operador:" value={section.operador} />
            <InfoRow label="Propriedade:" value={section.propriedade} />
            {!section.isTratamentoSementes && (
              <InfoRow label="Talhão:" value={section.talhao} />
            )}
            <InfoRow label="Cultura:" value={section.cultura} />
            {!section.isTratamentoSementes && (
              <InfoRow label="Área Total:" value={section.area} />
            )}

            {section.sementes && (
              <View style={styles.table}>
                <View style={styles.tableRow}>
                  <Text style={[styles.colSeed, styles.tableHeaderText]}>
                    Semente Origem
                  </Text>
                  <Text style={[styles.colSeedDose, styles.tableHeaderText]}>
                    Semente Destino
                  </Text>
                  <Text style={[styles.colSeedTotal, styles.tableHeaderText]}>
                    Total
                  </Text>
                </View>
                <View style={styles.tableRow}>
                  <Text style={styles.colSeed}>{section.sementes.origem}</Text>
                  <Text style={styles.colSeedDose}>
                    {section.sementes.destino}
                  </Text>
                  <Text style={styles.colSeedTotal}>
                    {section.sementes.quantidadeTotal}
                  </Text>
                </View>
              </View>
            )}

            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={[styles.colProduto, styles.tableHeaderText]}>
                  Produto
                </Text>
                <Text style={[styles.colDose, styles.tableHeaderText]}>
                  Dose
                </Text>
                <Text style={[styles.colTotal, styles.tableHeaderText]}>
                  Total
                </Text>
              </View>
              {section.produtos.map((produto) => (
                <View style={styles.tableRow} key={produto.descricao}>
                  <Text style={styles.colProduto}>{produto.descricao}</Text>
                  <Text style={styles.colDose}>{produto.dose}</Text>
                  <Text style={styles.colTotal}>{produto.quantidadeTotal}</Text>
                </View>
              ))}
            </View>
            {section.observacao && (
              <InfoRow label="Observação:" value={section.observacao} />
            )}
          </View>
        ))}

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default PendingAplicationReportDoc;
